import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2, CalendarCheck, MessagesSquare, Rocket } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { usePageMeta } from "@/utils/pageMeta";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const activities = [
  "Quad / Buggy",
  "Jet-ski / Activités nautiques",
  "Location de bateaux",
  "Surf / Plongée",
  "Karting / Loisirs",
  "Excursions / Tours",
  "Balades à cheval / chameau",
  "Autre activité",
];

const steps = [
  { icon: MessagesSquare, text: "Vous nous présentez votre activité et vos besoins." },
  { icon: CalendarCheck, text: "Nous organisons une démonstration adaptée à votre exploitation." },
  { icon: Rocket, text: "Votre espace ActiPilote est configuré avec vous, étape par étape." },
];

const inputCls = "w-full rounded-xl border border-slate-200 bg-mist/50 px-4 py-3 text-sm text-navy outline-none transition-all placeholder:text-navy/35 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/15";

const Contact = () => {
  usePageMeta(
    "Contact & démo — ActiPilote",
    "Demandez une démonstration d'ActiPilote : la plateforme de gestion tout-en-un pour les professionnels des activités touristiques et de loisirs."
  );

  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", email: "", phone: "", activity: "", message: "", website: "" });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("sujet") === "assistant-ia") {
      setForm((f) => ({ ...f, message: f.message || "Bonjour, je souhaite être informé du lancement de l'Assistant IA WhatsApp." }));
    }
  }, [searchParams]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        first_name: form.firstName,
        last_name: form.lastName,
        company: form.company,
        email: form.email,
        phone: form.phone,
        activity: form.activity,
        message: form.message,
        website: form.website,
      });
      trackEvent("generate_lead", { form: "contact" });
      setSent(true);
    } catch (err) {
      toast.error("L'envoi a échoué. Réessayez ou écrivez-nous à contact@actipilote.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-page" className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-24">
      <div className="bg-grid-faint pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full bg-brand/10 blur-[110px]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
            Demander une démo.
          </h1>
          <p className="mt-5 max-w-md text-base leading-relaxed text-navy/60 sm:text-lg">
            Parlez-nous de votre activité : nous vous montrons comment ActiPilote
            centralise votre exploitation, avec vos propres cas d'usage.
          </p>

          <div className="mt-10 space-y-5">
            {steps.map((s, i) => (
              <div key={s.text} className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <s.icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-mono text-[11px] font-bold text-navy/30">0{i + 1}</p>
                  <p className="text-sm font-semibold text-navy/80">{s.text}</p>
                </div>
              </div>
            ))}
          </div>

          <a href="mailto:contact@actipilote.com" data-testid="contact-email-link" className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline">
            <Mail className="h-4 w-4" /> contact@actipilote.com
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-lift sm:p-9">
            {sent ? (
              <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-turquoise/10 text-turquoise">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 text-2xl font-bold text-navy">Demande envoyée.</h2>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-navy/60">
                  Merci {form.firstName}. Votre demande a bien été transmise à notre équipe :
                  nous revenons vers vous rapidement à l'adresse <span className="font-semibold text-navy">{form.email}</span>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-lastname" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Nom *</label>
                    <input id="contact-lastname" data-testid="contact-lastname-input" required minLength={1} value={form.lastName} onChange={update("lastName")} placeholder="Alaoui" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-firstname" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Prénom *</label>
                    <input id="contact-firstname" data-testid="contact-firstname-input" required minLength={1} value={form.firstName} onChange={update("firstName")} placeholder="Yassine" className={inputCls} />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">E-mail professionnel *</label>
                    <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={update("email")} placeholder="vous@entreprise.com" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Téléphone</label>
                    <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={update("phone")} placeholder="+33 6 12 34 56 78" className={inputCls} />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-company" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Entreprise</label>
                    <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={update("company")} placeholder="Atlas Quad Aventure" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-activity" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Type d'activité</label>
                    <select id="contact-activity" data-testid="contact-activity-select" value={form.activity} onChange={update("activity")} className={inputCls}>
                      <option value="">Sélectionner…</option>
                      {activities.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <input type="text" name="website" value={form.website} onChange={update("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="contact-honeypot" />
                <div>
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-navy/50">Votre message *</label>
                  <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} rows={5} value={form.message} onChange={update("message")} placeholder="Décrivez votre activité, vos établissements et ce que vous attendez d'ActiPilote…" className={`${inputCls} resize-none`} />
                </div>
                <button type="submit" data-testid="contact-form-submit" disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60">
                  {loading ? "Envoi en cours…" : "Demander une démo"}
                  {!loading && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />}
                </button>
                <p className="text-center text-[11px] leading-relaxed text-navy/40">
                  Vos informations sont utilisées uniquement pour traiter votre demande.
                  Consultez notre <a href="/privacy" className="font-semibold text-navy/60 hover:text-brand">politique de confidentialité</a>.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
